import { BellIcon } from '@phosphor-icons/react'
import { useNavigate } from 'react-router'

import { cn } from '@/lib/cn'
import { haptic } from '@/lib/haptics'
import { useAvisos } from '@/lib/queries'

/**
 * La campanita del inicio.
 *
 * Vive en su propio archivo y no suelta dentro del inicio por una razón
 * concreta: necesita consultar la bandeja para saber si hay algo, y meter esa
 * consulta en el tablero mezclaría dos pantallas que se editan por separado.
 * Aquí el botón, su insignia y su fuente de verdad viajan juntos.
 *
 * La insignia sale de `resumen`, nunca de un literal:
 *
 * - `sinVer` decide **si** se enciende. Es lo que apareció desde la última vez
 *   que se abrió la bandeja, así que al leerla se apaga sola.
 * - `pendientes` decide **qué dice**. Es lo que pide una acción, que es el
 *   único número que alguien querría ver en una campanita: contar actividad
 *   —«tu pareja registró un café»— daría una cifra que nunca baja al actuar.
 *
 * Cuando no hay nada, no hay punto. Un indicador permanentemente encendido deja
 * de indicar en tres días.
 */
export function CampanaAvisos({
  className,
  sobreTinta = false,
}: {
  className?: string
  /**
   * Sobre el bloque de tinta del inicio. Sin esto la campana se dibujaba en
   * tinta sobre tinta —invisible— y el anillo de la insignia en color papel,
   * un halo claro sin sentido sobre fondo oscuro.
   */
  sobreTinta?: boolean
}) {
  const navigate = useNavigate()
  const { data } = useAvisos()

  const sinVer = data?.resumen.sinVer ?? 0
  const pendientes = data?.resumen.pendientes ?? 0

  // Dos dígitos es el tope: a partir de ahí el número no cabe en el punto sin
  // convertirlo en una pastilla que tapa el icono. «+9» dice lo mismo.
  const cifra = pendientes > 0 ? (pendientes > 9 ? '+9' : String(pendientes)) : null

  return (
    <button
      type="button"
      onClick={() => {
        haptic('light')
        navigate('/avisos')
      }}
      aria-label={
        sinVer === 0
          ? 'Avisos'
          : pendientes > 0
            ? `Avisos: ${pendientes} ${pendientes === 1 ? 'pendiente' : 'pendientes'}`
            : 'Avisos: hay novedades'
      }
      className={cn(
        'relative flex size-11 shrink-0 items-center justify-center rounded-full active:opacity-60',
        sobreTinta ? 'text-on-navy' : 'text-ink',
        className,
      )}
    >
      <BellIcon size={19} />

      {sinVer > 0 ? (
        <span
          aria-hidden
          className={cn(
            'absolute flex items-center justify-center rounded-full bg-expense',
            'font-mono text-[9px] font-bold leading-none text-white',
            // El anillo del color del fondo recorta el punto contra el icono
            // para que se lea como insignia y no como suciedad de la pantalla.
            sobreTinta ? 'ring-2 ring-navy' : 'ring-2 ring-[var(--papel,var(--bg))]',
            cifra
              ? 'right-1 top-1 h-[15px] min-w-[15px] px-1'
              : 'right-2.5 top-2.5 size-[7px]',
          )}
        >
          {cifra}
        </span>
      ) : null}
    </button>
  )
}
